import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import type {
  PropertyAssessmentResponse as PropertyAssessmentData,
  PropertyDetails,
  Assessment,
  TaxBreakdown,
  Jurisdiction,
  PropertyFlag,
} from '@kin/property-tax-types';

export type { PropertyAssessmentData, PropertyDetails, Assessment, TaxBreakdown, Jurisdiction, PropertyFlag };

const FEE = 50;

@Injectable({ providedIn: 'root' })
export class PropertyAssessmentService {
  private http = inject(HttpClient);

  private _data: PropertyAssessmentData | null = null;

  get data(): PropertyAssessmentData | null {
    return this._data;
  }

  /** e.g. "4 bed · 2.5 ba · 2,300 sq ft · Built 1970" */
  get displayPropertyDetails(): string {
    if (!this._data) return '';
    const p = this._data.property;
    const baths = p.halfBaths > 0 ? `${p.fullBaths}.5` : `${p.fullBaths}`;
    return `${p.bedrooms} bed · ${baths} ba · ${p.sqft.toLocaleString()} sq ft · Built ${p.yearBuilt}`;
  }

  /** Net savings after fee */
  get netSavings(): number {
    const savings = this._data?.taxBreakdown.estimatedSavings;
    if (savings == null) return 0;
    return savings - FEE;
  }

  /** Composite rate as display string, e.g. "2.09%" */
  get displayCompositeRate(): string {
    const rate = this._data?.taxBreakdown.compositeRate;
    if (rate == null) return '';
    return `${(rate * 100).toFixed(2)}%`;
  }

  /** Tax subtotal before exemption: assessed * rate */
  get currentTaxSubtotal(): number {
    if (!this._data) return 0;
    return Math.round(this._data.assessment.assessedValue * (this._data.taxBreakdown.compositeRate ?? 0));
  }

  /** Tax subtotal after appeal: market * rate */
  get projectedTaxSubtotal(): number {
    if (!this._data) return 0;
    return Math.round(this._data.assessment.marketValueEstimate * (this._data.taxBreakdown.compositeRate ?? 0));
  }

  /** Subtotal savings */
  get subtotalSavings(): number {
    return this.currentTaxSubtotal - this.projectedTaxSubtotal;
  }

  clear(): void {
    this._data = null;
  }

  loadAssessment(): Observable<PropertyAssessmentData | null> {
    if (this._data) return of(this._data);
    return this.http.get<PropertyAssessmentData>(`${environment.apiUrl}/property/assessment`).pipe(
      tap(data => (this._data = data)),
    );
  }
}
